import {
  Calendar,
  MapPin,
  Clock,
  Save,
  X,
  AlertCircle,
  Loader,
} from 'lucide-react';
import {
  FlowOption,
  FlowSelectSearchable,
} from '../../components/FlowSelectSearchable';
import { emailTemplateApi, eventApi } from '../../services/api';
import { Controller, useForm } from 'react-hook-form';
import {
  BasicFlow,
  EmailTemplate,
  EventFlowType,
  EventWithFlows,
} from '@dynamic-flow/types';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toLocalDateTimeInputValue } from '@/utils/date';
import * as Joi from 'joi';
import { getFormUrl } from '@/config/appConfig';
import { GenericSelect } from '@dynamic-flow/ui';

export const eventSchema = Joi.object({
  name: Joi.string().min(1).max(100).required(),
  description: Joi.string().max(1000).allow(''),
  location: Joi.string().min(1).max(50).required(),
  startDateTime: Joi.date().required(),
  endDateTime: Joi.date().greater(Joi.ref('startDateTime')).required(),
  emailTemplateCodename: Joi.string().max(100).required(),
  flows: Joi.array()
    .items(
      Joi.object({
        id: Joi.string().uuid().required(),
        type: Joi.string().valid('feedback', 'registration').required(),
        title: Joi.string().required(),
      })
        .unknown(true) // allow extra fields not listed
        .allow(null) // allow null if user resets
    )
    .min(1)
    .required()
    .unique((a, b) => a?.id === b?.id), // ensure unique flow ids
});

export type EventInput = {
  name: string;
  description: string;
  location: string;
  startDateTime: Date;
  endDateTime: Date;
  flows: FlowOption[];
  emailTemplateCodename: string;
};

// Form data type for datetime-local inputs (uses strings)
type EventFormData = {
  name: string;
  description: string;
  location: string;
  startDateTime: string;
  endDateTime: string;
  flows: ((FlowOption & { type: EventFlowType }) | null)[];
  emailTemplateCodename: string;
};

/** UI slots: flows[0]=feedback, flows[1]=registration — ignore API array order */
function normalizeEventFlows(
  flows: BasicFlow[] | undefined
): ((FlowOption & { type: EventFlowType }) | null)[] {
  const toFlowOption = (
    f: BasicFlow
  ): FlowOption & { type: EventFlowType } => ({
    id: f.id,
    title: f.title,
    type: f.type,
    status: 'published',
    publicUrl: f.publicUrl || getFormUrl(f.id, 'public'),
  });

  const feedback = flows?.find((f) => f.type === 'feedback');
  const registration = flows?.find((f) => f.type === 'registration');

  return [
    feedback ? toFlowOption(feedback) : null,
    registration ? toFlowOption(registration) : null,
  ];
}

const EventBuilder = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const isEditMode = Boolean(eventId);

  const [event, setEvent] = useState<EventWithFlows | null>(null);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const {
    handleSubmit,
    register,
    reset,
    getValues,
    setValue,
    formState: { errors, isSubmitting, isDirty },
    control,
    watch,
  } = useForm<EventFormData>({
    defaultValues: {
      name: '',
      location: '',
      description: '',
      startDateTime: '',
      endDateTime: '',
      emailTemplateCodename: '',
    },
  });

  // Convert form data to EventInput for API calls
  const convertToEventInput = (formData: EventFormData): EventInput => {
    return {
      name: formData.name,
      description: formData.description,
      location: formData.location,
      startDateTime: new Date(formData.startDateTime),
      endDateTime: new Date(formData.endDateTime),
      flows: formData.flows?.filter(Boolean) as FlowOption[],
      emailTemplateCodename: formData.emailTemplateCodename,
    };
  };

  // Load event data when in edit mode
  useEffect(() => {
    if (isEditMode && eventId) {
      loadEventData();
    }
  }, [isEditMode, eventId]);

  useEffect(() => {
    const loadEmailTemplates = async () => {
      try {
        const templates = await emailTemplateApi.getAll();
        if (Array.isArray(templates)) {
          setEmailTemplates(templates);
        }
      } catch (error) {
        console.error('Failed to load email templates:', error);
      }
    };
    loadEmailTemplates();
  }, []);

  const loadEventData = async () => {
    if (!eventId) return;

    setIsLoading(true);
    setLoadError(null);

    try {
      const eventPromise = eventApi.getById(eventId);
      const emailTemplatesPromise = await emailTemplateApi.getAll();

      const [eventData, emailTemplates] = await Promise.all([
        eventPromise,
        emailTemplatesPromise,
      ]);

      if (Array.isArray(emailTemplates)) {
        setEmailTemplates(emailTemplates);
      }

      if (eventData) {
        setEvent(eventData);

        // Populate form with existing data
        reset({
          name: eventData.name || '',
          location: eventData.location || '',
          description: eventData.description || '',
          startDateTime: eventData.startDateTime
            ? toLocalDateTimeInputValue(eventData?.startDateTime)
            : '',
          endDateTime: eventData.endDateTime
            ? toLocalDateTimeInputValue(eventData?.endDateTime)
            : '',
          flows: normalizeEventFlows(eventData?.flows),
          emailTemplateCodename: eventData.emailTemplateCodename || '',
        });
      }
    } catch (error) {
      console.error('Failed to load event:', error);
      setLoadError('Failed to load event data');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (formData: EventFormData) => {
    try {
      // Validate form data
      const eventData = convertToEventInput(formData);
      const { error } = eventSchema.validate(eventData, { abortEarly: false });

      if (error) {
        const errorMessages = error.details
          .map((detail) => detail.message)
          .join('\n');
        alert(`Validation errors:\n${errorMessages}`);
        return;
      }

      if (isEditMode && eventId) {
        // Update existing event
        const rep = await eventApi.update(eventId, eventData);
        console.log(rep);
        alert('Event updated successfully!');
      } else {
        // Create new event
        const rep = await eventApi.create(eventData);
        console.log(rep);
        alert('Event created successfully!');
      }

      // Go back to site origin
      window.location.href = window.location.origin;
    } catch (error) {
      const action = isEditMode ? 'update' : 'create';
      alert(`Failed to ${action} event. Please try again.`);
    }
  };

  const handleReset = () => {
    if (isDirty) {
      const shouldReset = confirm(
        'Are you sure you want to reset the form? All changes will be lost.'
      );
      if (!shouldReset) return; // user canceled, stop here
    }

    if (isEditMode && event) {
      // Reset to original event data
      reset({
        name: event.name || '',
        location: event.location || '',
        description: event.description || '',
        startDateTime: event.startDateTime
          ? toLocalDateTimeInputValue(event.startDateTime)
          : '',
        endDateTime: event.endDateTime
          ? toLocalDateTimeInputValue(event.endDateTime)
          : '',
        flows: normalizeEventFlows(event.flows),
        emailTemplateCodename: event.emailTemplateCodename || '',
      });
    } else {
      // Reset to default values for create mode
      reset();
    }
  };

  // Show loading state while fetching event data
  if (isEditMode && isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
              <p className="text-gray-600">Loading event data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if failed to load event data
  if (isEditMode && loadError) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">⚠️ Error loading event</div>
            <p className="text-gray-500 text-sm mb-4">{loadError}</p>
            <button
              onClick={loadEventData}
              className="text-blue-600 hover:text-blue-700 text-sm"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Event' : 'Create New Event'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isEditMode
              ? 'Update the details below to modify your event'
              : 'Fill in the details below to create a new event'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="bg-white shadow-lg rounded-lg">
            {/* Form Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700">
              <h2 className="text-xl font-semibold text-white">
                Event Details
              </h2>
              <p className="text-blue-100 text-sm mt-1">
                Provide the basic information about your event
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Event Name */}
              <div>
                <label
                  htmlFor="eventName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Event Name *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="eventName"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.name
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    placeholder="Enter event name"
                    {...register('name', {
                      required: 'Event name is required',
                      maxLength: 100,
                    })}
                  />
                  {errors.name && (
                    <div className="absolute right-3 top-3">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                  )}
                </div>
                {errors.name?.message && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Event Location */}
              <div>
                <label
                  htmlFor="location"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Location *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="location"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.location
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    placeholder="Enter event location (e.g., Conference Center, Room 101)"
                    {...register('location', {
                      required: 'Location is required',
                      maxLength: 50,
                    })}
                  />
                  {errors.location && (
                    <div className="absolute right-3 top-3">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                  )}
                </div>
                {errors.location?.message && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.location?.message}
                  </p>
                )}
              </div>

              {/* Event Description */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Event Description
                </label>
                <textarea
                  id="description"
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                  placeholder="Describe your event, agenda, speakers, or any other relevant details..."
                  {...register('description', {
                    required: 'Description is required',
                    maxLength: 1000,
                  })}
                />
                <p className="mt-1 text-sm text-gray-500">
                  {watch('description', '').length}/1000 characters
                </p>
              </div>

              {/* Date and Time Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Start Date Time */}
                <div>
                  <label
                    htmlFor="startDateTime"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Start Date & Time *
                  </label>
                  <div className="relative">
                    <input
                      type="datetime-local"
                      id="startDateTime"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.startDateTime
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                      {...register('startDateTime', {
                        required: 'Start date is required',
                        validate: {
                          beforeEnd: (v) =>
                            new Date(v) <= new Date(getValues('endDateTime')) ||
                            'Start date must be before or equal to end date',
                        },
                      })}
                    />
                  </div>
                  {errors.startDateTime && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.startDateTime?.message}
                    </p>
                  )}
                </div>

                {/* End Date Time */}
                <div>
                  <label
                    htmlFor="endDateTime"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    <Clock className="w-4 h-4 inline mr-1" />
                    End Date & Time *
                  </label>
                  <div className="relative">
                    <input
                      type="datetime-local"
                      id="endDateTime"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.endDateTime
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                      {...register('endDateTime', {
                        required: 'End date is required',
                        validate: {
                          afterStart: (v) =>
                            new Date(v) >=
                              new Date(getValues('startDateTime')) ||
                            'End date must be after or equal to start date',
                        },
                      })}
                    />
                  </div>
                  {errors.endDateTime && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.endDateTime?.message}
                    </p>
                  )}
                </div>
              </div>

              {/* URLs Section */}
              <div className="border-t pt-6">
                <div className="space-y-6">
                  {/* Feedback Flow Selection */}
                  <div>
                    <Controller
                      control={control}
                      name="flows.0"
                      rules={{
                        required: 'Feedback flow is required',
                        validate: (value) => {
                          const flows = getValues('flows');
                          if (!value) return true; // allow undefined if optional
                          if (flows[1] && flows[1]?.id === value.id) {
                            return 'Feedback and registration flows must be different';
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => {
                        return (
                          <FlowSelectSearchable
                            label="Feedback Form"
                            description="Select a published flow for collecting feedback"
                            error={error?.message}
                            placeholder="Select a feedback form..."
                            value={field?.value}
                            onChange={(flow) => {
                              setValue(
                                'flows.0',
                                flow
                                  ? {
                                      ...(flow as FlowOption),
                                      type: 'feedback',
                                    }
                                  : null,
                                { shouldDirty: true }
                              );
                            }}
                          />
                        );
                      }}
                    />
                  </div>

                  {/* Registration Flow Selection */}
                  <div>
                    <Controller
                      control={control}
                      name="flows.1"
                      rules={{
                        required: 'Registration flow is required',
                        validate: (value) => {
                          const flows = getValues('flows');
                          if (!value) return true; // allow undefined if optional
                          if (flows[0] && flows[0]?.id === value.id) {
                            return 'Feedback and registration flows must be different';
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => {
                        return (
                          <FlowSelectSearchable
                            label="Registration Form"
                            description="Select a published flow for collecting registration"
                            error={error?.message}
                            placeholder="Select a registration form..."
                            value={field.value}
                            onChange={(flow) => {
                              setValue(
                                'flows.1',
                                flow
                                  ? {
                                      ...(flow as FlowOption),
                                      type: 'registration',
                                    }
                                  : null,
                                { shouldDirty: true }
                              );
                            }}
                          />
                        );
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select email template
                    </label>
                    <div className="max-h-48 overflow-y-auto">
                      <Controller
                        control={control}
                        name="emailTemplateCodename"
                        rules={{ required: 'Email template is required' }}
                        render={({ field, fieldState: { error } }) => {
                          return (
                            <GenericSelect
                              {...field}
                              value={field.value ?? ''}
                              items={emailTemplates}
                              label="Email templates"
                              placeholder="Select template..."
                              onValueChange={(value) => field.onChange(value)}
                              error={error?.message}
                            />
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
              <button
                type="button"
                onClick={handleReset}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Reset
              </button>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting || !isDirty}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {isEditMode ? 'Updating Event...' : 'Creating Event...'}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {isEditMode ? 'Update Event' : 'Create Event'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventBuilder;
